import { isAdminPermission } from '@/shared/auth/has-any-authority';
import { CALL_CENTER_IAM_WRITE_FLAGS } from '@/shared/constants/call-center-iam';
import { readPagedRows } from '@/shared/utils/spring-api';
function normalizePermission(raw) {
    if (typeof raw === 'string') {
        return { action: raw, description: raw, active: true };
    }
    if (!raw || typeof raw !== 'object')
        return null;
    const o = raw;
    const action = String(o.action ?? o.name ?? o.permission ?? o.authority ?? '').trim();
    if (!action && o.id == null)
        return null;
    return {
        id: o.id,
        action: action || undefined,
        description: typeof o.description === 'string'
            ? o.description
            : typeof o.label === 'string'
                ? o.label
                : action || undefined,
        active: o.active !== false,
    };
}
function normalizePermissions(raw) {
    if (!Array.isArray(raw))
        return [];
    return raw.map(normalizePermission).filter((p) => p !== null);
}
export function normalizeIamRolesList(data) {
    if (data == null)
        return [];
    let list = data;
    if (typeof list === 'object' && list !== null && 'data' in list) {
        const inner = list.data;
        if (Array.isArray(inner))
            list = inner;
        else if (inner && typeof inner === 'object' && 'content' in inner) {
            list = inner.content ?? [];
        }
    }
    if (!Array.isArray(list) && typeof list === 'object' && list !== null && 'content' in list) {
        list = list.content ?? [];
    }
    if (!Array.isArray(list))
        return [];
    return list;
}
function filterIamRoles(list, filter) {
    const q = filter?.trim().toLowerCase();
    if (!q)
        return list;
    return list.filter((item) => {
        const name = String(item.name ?? '').toLowerCase();
        const description = String(item.description ?? '').toLowerCase();
        return name.includes(q) || description.includes(q);
    });
}
export function readIamRolesPage(data, headers, filter) {
    const list = normalizeIamRolesList(data);
    if (Array.isArray(data)) {
        const filtered = filterIamRoles(list, filter);
        const paged = readPagedRows(filtered, headers);
        return { rows: paged.rows, total: filter?.trim() ? filtered.length : paged.total };
    }
    if (data && typeof data === 'object' && 'content' in data) {
        return readPagedRows(data, headers);
    }
    const filtered = filterIamRoles(list, filter);
    return { rows: filtered, total: filtered.length };
}
/** Roles named `ROLE_ADMIN` or holding the `ROLE_ADMIN` permission cannot be deleted. */
export function isProtectedAdminRole(role) {
    if (!role)
        return false;
    const roleName = String(role.roleName ?? role.name ?? '').trim();
    if (isAdminPermission(roleName))
        return true;
    return normalizePermissions(role.permissions).some((permission) => isAdminPermission(String(permission.action ?? '')));
}
/** Map IAM role → UI model (`roleName` for existing screens). */
export function fromIamRole(dto) {
    const name = dto.name ?? '';
    return {
        id: dto.id,
        name,
        roleName: name,
        description: dto.description ?? '',
        active: dto.active !== false,
        isCallCenterRelated: dto.isCallCenterRelated !== false,
        permissions: normalizePermissions(dto.permissions),
        createdBy: dto.createdBy,
        createdDate: dto.createdDate ? new Date(dto.createdDate) : null,
        lastModifiedBy: dto.lastModifiedBy,
        lastModifiedDate: dto.lastModifiedDate ? new Date(dto.lastModifiedDate) : null,
    };
}
function toIamPermissionPayload(perm) {
    const action = String(perm.action ?? '').trim();
    return {
        id: perm.id ?? action,
        action,
        description: perm.description ?? null,
        active: perm.active !== false,
    };
}
/** Map UI form → IAM POST/PUT body (`name` + permission objects). */
export function toIamRolePayload(role) {
    const permissions = normalizePermissions(role.permissions).map(toIamPermissionPayload);
    return {
        ...(role.id != null && role.id !== '' ? { id: role.id } : {}),
        name: String(role.roleName ?? role.name ?? '').trim(),
        active: role.active !== false,
        description: role.description ?? '',
        isCallCenterRelated: role.isCallCenterRelated ?? CALL_CENTER_IAM_WRITE_FLAGS.isCallCenterRelated,
        permissions,
    };
}
